import { useEffect, useMemo } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { AiAssistant } from '@/features/insights/components/ai-assistant';
import { Anomalies } from '@/features/insights/components/anomalies';
import { BlurredContainer } from '@/features/insights/components/blurred-container';
import { ChartsRow } from '@/features/insights/components/charts-row';
import { InsightsHubFilter } from '@/features/insights/components/insights-hub-filter';
import { KpiSelectionRow } from '@/features/insights/components/kpi-selection-row';
import { useInsightsProjectsFiltered } from '@/features/insights/hooks/use-insights-projects-filtered';
import { useKpiAnomalies } from '@/features/insights/hooks/use-kpi-anomalies';
import { useInsightsFilterStore } from '@/features/insights/store/use-insights-hub-filter';
import { useInsightsKpiStore } from '@/features/insights/store/use-insights-kpi-store';
import { useProjectKpisWithMetrics } from '@/features/projects/project-details/hooks/use-project-kpis';
import { getKpiUniqueId } from '@/features/projects/shared/utils/kpi-utils';
import InfoPurple from '@/shared/assets/info-purple.svg?react';
import { DefaultLayout } from '@/shared/components/layouts/default-layout';
import { CenteredLoadingIndicator } from '@/shared/components/loading-indicator';
import { EmptyTablePlaceholderContent } from '@/shared/components/table/empty-table-placeholder';
import { Button } from '@/shared/components/ui/button';

export const InsightsHubPage = () => {
  const [searchParams] = useSearchParams();
  const kpiUniqueIdParam = searchParams.get('kpiUniqueId');
  const projectIdParam = searchParams.get('projectId');

  const { data: projects, isLoading, isError } = useInsightsProjectsFiltered();
  const { t } = useTranslation('insight', { keyPrefix: 'InsightsHub' });
  const { projectIds, setProjectIds, subprojectIds, fromDate, toDate } =
    useInsightsFilterStore();

  const fromDateObj = fromDate ? new Date(fromDate) : undefined;
  const toDateObj = toDate ? new Date(toDate) : undefined;
  const { uniqueKpiId, setUniqueKpiId } = useInsightsKpiStore();

  // should only be one in the list
  const projectId = subprojectIds?.length
    ? Number(subprojectIds[0])
    : projectIds?.length
      ? Number(projectIds[0])
      : projects?.[0]?.id;
  const subprojectId = subprojectIds?.length
    ? Number(subprojectIds[0])
    : undefined;

  useEffect(() => {
    if (projectIdParam) {
      setProjectIds([projectIdParam]);
    }
  }, [projectIdParam, setProjectIds]);

  // Auto-select first project if none selected
  useEffect(() => {
    if (!projectIds?.length && projects && projects.length > 0) {
      setProjectIds([projects[0].id.toString()]);
    }
  }, [projectIds, projects, setProjectIds]);

  const matchingProject = projects?.find((p) => {
    // If subprojectId is set, try to find it first
    if (subprojectId && p.id === subprojectId) {
      return true;
    }
    // If projectId is set, try to find it
    if (projectId && p.id === projectId) {
      return true;
    }
    return false;
  });

  const [projectKpisQuery, derivativeKpisQuery] = useProjectKpisWithMetrics({
    projectId: matchingProject?.id,
    projectType: matchingProject?.type,
    enabled: !!matchingProject,
  });

  const kpis = useMemo(
    () => [
      ...(projectKpisQuery.data?.filter((kpi) => kpi != null) || []),
      ...(derivativeKpisQuery.data?.filter((kpi) => kpi != null) || []),
    ],
    [projectKpisQuery.data, derivativeKpisQuery.data],
  );

  const currentKpi = useMemo(() => {
    return kpis?.find((k) => getKpiUniqueId(k) === uniqueKpiId) || kpis?.[0];
  }, [kpis, uniqueKpiId]);

  useEffect(() => {
    if (kpiUniqueIdParam) {
      const kpi = kpis?.find((k) => getKpiUniqueId(k) === kpiUniqueIdParam);
      if (kpi) {
        setUniqueKpiId(getKpiUniqueId(kpi));
      }
    }
  }, [kpiUniqueIdParam, setUniqueKpiId, kpis]);

  const hasNoData =
    (projects && projects.length === 0) || !kpis || kpis.length === 0;

  const effectiveProjectId = subprojectId || projectId;

  const { data: anomalies, isLoading: isLoadingAnomalies } = useKpiAnomalies({
    kpiId: currentKpi?.id!,
    projectId: effectiveProjectId ?? 0,
    monthsBack: 12,
    periodType: 'monthly',
    fromDate: fromDateObj,
    toDate: toDateObj,
  });

  return (
    <DefaultLayout
      title={t('title')}
      titleBig
      headerActions={isLoading ? [] : [<InsightsHubFilter />]}
    >
      {isLoading ? (
        <CenteredLoadingIndicator />
      ) : !projects || isError ? (
        <span>{t('errors.fetch_projects')}</span>
      ) : (
        <div className="mb-4 flex flex-col gap-6">
          <BlurredContainer isBlurred={hasNoData}>
            <KpiSelectionRow
              kpis={matchingProject ? kpis : undefined}
              currentKpi={currentKpi}
              setKpiId={setUniqueKpiId}
            />
          </BlurredContainer>

          {projectKpisQuery.isLoading || derivativeKpisQuery.isLoading ? (
            <CenteredLoadingIndicator />
          ) : (
            <>
              <div className="relative">
                <BlurredContainer isBlurred={hasNoData}>
                  <ChartsRow
                    key={uniqueKpiId}
                    projectId={effectiveProjectId}
                    isSubprojectFiltered={
                      Boolean(subprojectId) ||
                      matchingProject?.type === 'subproject_contractor' ||
                      matchingProject?.type === 'subproject_client'
                    }
                    kpi={currentKpi}
                    anomalies={anomalies}
                    fromDate={fromDateObj}
                    toDate={toDateObj}
                  />
                </BlurredContainer>
                {hasNoData && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <NoInsightsPlaceholder />
                  </div>
                )}
              </div>{' '}
              <div className="flex flex-col gap-6 lg:flex-row">
                <BlurredContainer isBlurred={hasNoData} className="w-full">
                  <Anomalies
                    anomalies={anomalies}
                    isLoading={isLoadingAnomalies}
                  />
                </BlurredContainer>
                <AiAssistant />
              </div>
            </>
          )}
        </div>
      )}
    </DefaultLayout>
  );
};

const NoInsightsPlaceholder = () => {
  const { t } = useTranslation('insight', { keyPrefix: 'InsightsHub' });
  return (
    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 pt-24">
      <Button
        variant={'outline'}
        btnType={'icon'}
        shape={'circle'}
        className="size-10 p-1"
      >
        <InfoPurple />
      </Button>
      <EmptyTablePlaceholderContent
        className="w-[200px] p-0 md:w-[300px]"
        title={t('errors.no_insights_available')}
        description={t('errors.no_insights_available_description')}
        centered
      />
    </div>
  );
};
